/**
 * ChangeServiceDialog: one dialog, one POST, one transaction for the
 * client's service, the lead care manager and the lead SPICE therapist.
 * Opened from the record menu's Change service and from the strip's SPICE
 * therapist door.
 *
 * Service decides which lead boxes show: Care manager with care
 * management (never for a SPICE-only viewer), SPICE therapist with SPICE.
 * Leaving a service hides its box and keeps its lead: a hidden care
 * manager box sends nothing, a hidden SPICE therapist box sends the stored
 * therapist (changeServiceBody). A lead missing from the assign-options
 * lists (departed, or no longer doing the service) shows as the selected
 * option under the record's name, so the select shows the truth.
 *
 * "Save service" is pressable at rest; pressed with nothing changed it
 * closes and sends nothing. A refused lead (422) reads its sentence under
 * its box, with focus there; anything else reads the house save sentence
 * on the dialog's alert line.
 */

import { useRef, useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '@/components/primitives/Button';
import { Dialog } from '@/components/primitives/Dialog';
import { Field } from '@/components/primitives/Field';
import { SelectField } from '@/components/primitives/SelectField';
import { useTenant } from '@/lib/auth';
import { useAssignOptions } from '@/lib/workspace';
import { useSetClientEngagement } from '@/lib/clientsApi';
import { changeServiceBody, includesSpice, showsCareManagerBox, withLead } from '@/lib/changeService';
import { changeServiceIntro } from '@/lib/recordFormsCopy';
import { errorCodeOf, pageWriteText } from '@/lib/errorCopy';
import { useSupportContact } from '@/hooks/useSupportContact';
import { isSpiceOnlyViewer } from '@/lib/spiceOnlyViewer';
import { ENGAGEMENT_LABELS } from '@/components/clientRecord/VitalsBand';
import type { ClientEngagement, VitalsBand as VitalsBandData } from '@/lib/clientRecord';

const CM_REFUSED = "That person isn't an active care manager. Pick someone else from the list.";
const THERAPIST_REFUSED = "That person isn't an active SPICE therapist. Pick someone else from the list.";

export function ChangeServiceDialog({
  vitals,
  onClose,
}: {
  vitals: Pick<VitalsBandData, 'engagement' | 'owner' | 'spiceTherapist' | 'pronouns'>;
  onClose: () => void;
}): ReactNode {
  const { tenantSlug = '', clientId = '' } = useParams<{ tenantSlug: string; clientId: string }>();
  const { whoami } = useTenant();
  const spiceOnlyViewer = isSpiceOnlyViewer(whoami);
  const { careManagers, spiceTherapists } = useAssignOptions(tenantSlug);
  const setEngagement = useSetClientEngagement(tenantSlug);
  const contact = useSupportContact();

  const [engagement, setEngagementValue] = useState<ClientEngagement>(vitals.engagement);
  const [cmId, setCmId] = useState(vitals.owner?.rosterId ?? '');
  const [therapistId, setTherapistId] = useState(vitals.spiceTherapist?.rosterId ?? '');
  const [cmError, setCmError] = useState<string | null>(null);
  const [therapistError, setTherapistError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const cmRef = useRef<HTMLSelectElement | null>(null);
  const therapistRef = useRef<HTMLSelectElement | null>(null);

  const showCm = showsCareManagerBox(engagement, spiceOnlyViewer);
  const showTherapist = includesSpice(engagement);

  async function pressSave(): Promise<void> {
    setSaveError(null);
    const body = changeServiceBody({
      stored: {
        engagement: vitals.engagement,
        primaryCmRosterId: vitals.owner?.rosterId ?? null,
        spiceTherapistRosterId: vitals.spiceTherapist?.rosterId ?? null,
      },
      chosen: { engagement, primaryCmRosterId: cmId, spiceTherapistRosterId: therapistId },
      spiceOnlyViewer,
    });
    if (body === null) {
      onClose();
      return;
    }
    try {
      await setEngagement.mutateAsync({
        id: clientId,
        engagement: body.engagement,
        spiceTherapistRosterId: body.spice_therapist_roster_id,
        ...('primary_cm_roster_id' in body ? { primaryCmRosterId: body.primary_cm_roster_id } : {}),
      });
      onClose();
    } catch (err) {
      const code = errorCodeOf(err);
      if (code === 'not_a_care_manager' && showCm) {
        setCmError(CM_REFUSED);
        cmRef.current?.focus();
        return;
      }
      if (code === 'not_a_spice_therapist' && showTherapist) {
        setTherapistError(THERAPIST_REFUSED);
        therapistRef.current?.focus();
        return;
      }
      setSaveError(pageWriteText(err, contact));
    }
  }

  return (
    <Dialog
      title="Change service"
      size="md"
      testId="change-service-dialog"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={setEngagement.isPending}
            loading={setEngagement.isPending}
            data-testid="change-service-save"
            onClick={() => void pressSave()}
          >
            Save service
          </Button>
        </>
      }
    >
      <p className="text-xs text-ink-soft mb-3">{changeServiceIntro(vitals.pronouns)}</p>
      <div className="flex flex-col gap-3">
        <Field label="Service" htmlFor="engagement-select">
          <SelectField
            size="field"
            id="engagement-select"
            data-testid="engagement-select"
            value={engagement}
            onChange={(e) => setEngagementValue(e.target.value as ClientEngagement)}
          >
            {(Object.keys(ENGAGEMENT_LABELS) as ClientEngagement[]).map((v) => (
              <option key={v} value={v}>
                {ENGAGEMENT_LABELS[v]}
              </option>
            ))}
          </SelectField>
        </Field>
        {showCm && (
          <Field label="Care manager" htmlFor="change-service-cm-select" error={cmError ?? undefined}>
            <SelectField
              size="field"
              ref={cmRef}
              id="change-service-cm-select"
              data-testid="change-service-cm-select"
              value={cmId}
              aria-invalid={cmError !== null ? 'true' : undefined}
              aria-describedby={cmError !== null ? 'change-service-cm-select-error' : undefined}
              onChange={(e) => {
                setCmError(null);
                setCmId(e.target.value);
              }}
            >
              <option value="">No care manager</option>
              {withLead(careManagers, vitals.owner).map((p) => (
                <option key={p.roster_id} value={p.roster_id}>
                  {p.name}
                </option>
              ))}
            </SelectField>
          </Field>
        )}
        {showTherapist && (
          <Field label="SPICE therapist" htmlFor="spice-therapist-select" error={therapistError ?? undefined}>
            <SelectField
              size="field"
              ref={therapistRef}
              id="spice-therapist-select"
              data-testid="spice-therapist-select"
              value={therapistId}
              aria-invalid={therapistError !== null ? 'true' : undefined}
              aria-describedby={therapistError !== null ? 'spice-therapist-select-error' : undefined}
              onChange={(e) => {
                setTherapistError(null);
                setTherapistId(e.target.value);
              }}
            >
              <option value="">No SPICE therapist</option>
              {withLead(spiceTherapists, vitals.spiceTherapist).map((p) => (
                <option key={p.roster_id} value={p.roster_id}>
                  {p.name}
                </option>
              ))}
            </SelectField>
          </Field>
        )}
        {saveError !== null && (
          <p role="alert" data-testid="change-service-error" className="text-xs text-pepper">
            {saveError}
          </p>
        )}
      </div>
    </Dialog>
  );
}
